import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vpat")({
  component: VpatPage,
});

const criteria = [
  { id: "1.1.1", name: "Non-text Content", level: "A", status: "Supports", notes: "All images have alt text, decorative SVGs use aria-hidden" },
  { id: "1.3.1", name: "Info and Relationships", level: "A", status: "Supports", notes: "Semantic HTML (h1\u2013h3), form labels, ARIA landmarks" },
  { id: "1.3.5", name: "Identify Input Purpose", level: "AA", status: "Supports", notes: "autoComplete attributes on auth + resume forms" },
  { id: "1.4.3", name: "Contrast (Minimum)", level: "AA", status: "Supports", notes: "All text meets 4.5:1 ratio" },
  { id: "1.4.11", name: "Non-text Contrast", level: "AA", status: "Supports", notes: "--border-input token meets 3:1" },
  { id: "2.1.1", name: "Keyboard", level: "A", status: "Supports", notes: "All interactive elements keyboard accessible" },
  { id: "2.4.1", name: "Bypass Blocks", level: "A", status: "Supports", notes: "Skip navigation link" },
  { id: "2.4.2", name: "Page Titled", level: "A", status: "Supports", notes: "Dynamic page titles via router" },
  { id: "2.4.3", name: "Focus Order", level: "A", status: "Supports", notes: "Dialog focus trap, sidebar focus management" },
  { id: "2.4.7", name: "Focus Visible", level: "AA", status: "Supports", notes: "focus-visible ring styling" },
  { id: "3.3.1", name: "Error Identification", level: "A", status: "Supports", notes: "aria-invalid + aria-describedby on form errors" },
  { id: "4.1.2", name: "Name, Role, Value", level: "A", status: "Supports", notes: "ARIA roles on dialogs, menus, toasts" },
  { id: "4.1.3", name: "Status Messages", level: "AA", status: "Supports", notes: "aria-live regions, route announcements" },
] as const;

function VpatPage() {
  return (
    <div
      className="mx-auto max-w-3xl px-6 py-16"
      style={{ color: "var(--text)" }}
    >
      <h1
        className="text-3xl font-bold tracking-tight"
        style={{ color: "var(--text)" }}
      >
        VPAT 2.5 &mdash; EU Edition
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        Alchema Accessibility Conformance Report
      </p>

      {/* Meta */}
      <dl
        className="mt-8 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm"
        style={{ color: "var(--text-secondary)" }}
      >
        <dt className="font-medium" style={{ color: "var(--text)" }}>Product</dt>
        <dd>Alchema (alchema.eu)</dd>
        <dt className="font-medium" style={{ color: "var(--text)" }}>Version</dt>
        <dd>3.2</dd>
        <dt className="font-medium" style={{ color: "var(--text)" }}>Report Date</dt>
        <dd>March 2026</dd>
        <dt className="font-medium" style={{ color: "var(--text)" }}>Standard</dt>
        <dd>EN 301 549 V3.2.1 (2021-03) incorporating WCAG 2.2 Level AA</dd>
      </dl>

      {/* Summary */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          Summary
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Alchema is <strong>partially conformant</strong> with WCAG 2.2
          Level AA. Most web content is accessible. Generated PDF documents are
          tagged but not yet fully PDF/UA-1 compliant. The Chrome extension has
          not been fully audited.
        </p>
      </section>

      {/* WCAG Table */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          Table 1: WCAG 2.2 Report
        </h2>
        <div className="mt-4 overflow-x-auto">
          <table
            className="w-full text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            <thead>
              <tr
                className="border-b text-left"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <th className="pb-2 pr-4 font-medium">Criteria</th>
                <th className="pb-2 pr-4 font-medium">Level</th>
                <th className="pb-2 pr-4 font-medium">Conformance</th>
                <th className="pb-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {criteria.map((c) => (
                <tr
                  key={c.id}
                  className="border-b"
                  style={{ borderColor: "var(--border-subtle)" }}
                >
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {c.id} {c.name}
                  </td>
                  <td className="py-2 pr-4">{c.level}</td>
                  <td className="py-2 pr-4">{c.status}</td>
                  <td className="py-2">{c.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Known Limitations */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          Known Limitations
        </h2>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <li>
            PDF/DOCX documents: Tagged but not fully PDF/UA-1 compliant
          </li>
          <li>Chrome extension: Partial audit only</li>
          <li>
            Drag-and-drop: @dnd-kit provides keyboard + SR support, but complex
            gestures may not be fully equivalent
          </li>
          <li>
            Spline 3D background: Hidden from assistive technology (aria-hidden)
          </li>
        </ul>
      </section>

      {/* Contact */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          Contact
        </h2>
        <p
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          For accessibility questions:{" "}
          <a
            href="mailto:barrierefreiheit@alchema.eu"
            className="underline underline-offset-2"
            style={{ color: "var(--accent)" }}
          >
            barrierefreiheit@alchema.eu
          </a>
        </p>
      </section>

      {/* Back links */}
      <div className="mt-16 flex gap-6">
        <Link
          to="/barrierefreiheit"
          className="text-sm underline underline-offset-2"
          style={{ color: "var(--accent)" }}
        >
          Barrierefreiheit (DE)
        </Link>
        <Link
          to="/"
          className="text-sm underline underline-offset-2"
          style={{ color: "var(--accent)" }}
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}
